import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { FIRE_COLORS } from './BlazeMaterial';

// Smoke particles drifting inside the board
const SmokeParticles = () => {
  const particlesRef = useRef();

  const particles = useMemo(() => {
    const data = [];
    for (let i = 0; i < 25; i++) {
      data.push({
        x: (Math.random() - 0.5) * 9,
        y: Math.random() * 20,
        z: -0.05 + Math.random() * 0.1,
        size: 0.3 + Math.random() * 0.4,
        speed: 0.2 + Math.random() * 0.3,
        phase: Math.random() * Math.PI * 2,
        drift: (Math.random() - 0.5) * 0.4,
        opacity: 0.08 + Math.random() * 0.08,
      });
    }
    return data;
  }, []);

  useFrame((state) => {
    if (!particlesRef.current) return;
    const time = state.clock.elapsedTime;

    particlesRef.current.children.forEach((particle, i) => {
      const data = particles[i];

      // Slow rising with gentle drift
      const y = (data.y + time * data.speed) % 22 - 1;
      particle.position.y = y;
      particle.position.x = data.x + Math.sin(time * 0.3 + data.phase) * data.drift;

      // Gentle rotation
      particle.rotation.z = Math.sin(time * 0.2 + data.phase) * 0.3;

      // Fade at edges
      const heightFade = y > 18 ? 1 - (y - 18) / 4 : (y < 1 ? y : 1);
      particle.material.opacity = Math.max(0, heightFade * data.opacity);
    });
  });

  return (
    <group ref={particlesRef}>
      {particles.map((data, i) => (
        <mesh key={i} position={[data.x, data.y, data.z]}>
          <circleGeometry args={[data.size, 8]} />
          <meshBasicMaterial
            color={FIRE_COLORS.smoke}
            transparent
            opacity={data.opacity}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
};

// Floating ember sparks
const EmberSparks = () => {
  const sparksRef = useRef();

  const sparks = useMemo(() => {
    const data = [];
    for (let i = 0; i < 12; i++) {
      data.push({
        x: (Math.random() - 0.5) * 9,
        y: Math.random() * 20,
        z: 0.05 + Math.random() * 0.1,
        speed: 0.8 + Math.random() * 0.6,
        size: 0.04 + Math.random() * 0.03,
        phase: Math.random() * Math.PI * 2,
      });
    }
    return data;
  }, []);

  useFrame((state) => {
    if (!sparksRef.current) return;
    const time = state.clock.elapsedTime;

    sparksRef.current.children.forEach((spark, i) => {
      const data = sparks[i];

      // Rising with slight wave
      const y = (data.y + time * data.speed) % 22 - 1;
      spark.position.y = y;
      spark.position.x = data.x + Math.sin(time * 2 + data.phase) * 0.15;

      // Flickering
      const flicker = Math.sin(time * 15 + data.phase) * 0.4 + 0.6;
      spark.material.opacity = flicker * (y > 0 ? 0.8 : 0.4);

      // Pulse size
      const pulse = 1 + Math.sin(time * 10 + data.phase) * 0.2;
      spark.scale.setScalar(pulse);
    });
  });

  return (
    <group ref={sparksRef}>
      {sparks.map((data, i) => (
        <mesh key={i} position={[data.x, data.y, data.z]}>
          <circleGeometry args={[data.size, 5]} />
          <meshBasicMaterial
            color={i % 3 === 0 ? FIRE_COLORS.flameCore : FIRE_COLORS.ember}
            transparent
            opacity={0.7}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

// Subtle heat shimmer
const HeatShimmer = () => {
  const shimmersRef = useRef([]);

  const shimmers = useMemo(() => {
    const data = [];
    for (let i = 0; i < 3; i++) {
      data.push({
        x: (i - 1) * 3,
        phase: (i / 3) * Math.PI * 2,
        width: 2 + Math.random() * 1,
      });
    }
    return data;
  }, []);

  useFrame((state) => {
    const time = state.clock.elapsedTime;

    shimmersRef.current.forEach((shimmer, i) => {
      if (!shimmer) return;
      const data = shimmers[i];

      // Rising wave
      const y = (time * 2 + data.phase * 3) % 24 - 2;
      shimmer.position.y = y;
      shimmer.position.x = data.x + Math.sin(time + data.phase) * 0.3;

      // Fade at edges
      const fade = y > 18 ? 1 - (y - 18) / 4 : (y < 0 ? (y + 2) / 2 : 1);
      shimmer.material.opacity = Math.max(0, fade * 0.06);
    });
  });

  return (
    <group>
      {shimmers.map((data, i) => (
        <mesh
          key={i}
          ref={(el) => shimmersRef.current[i] = el}
          position={[data.x, 0, -0.02]}
        >
          <planeGeometry args={[data.width, 0.4]} />
          <meshBasicMaterial
            color={FIRE_COLORS.heatDistort}
            transparent
            opacity={0.06}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

// Lava glow at bottom
const LavaGlow = ({ width }) => {
  const glowRef = useRef();

  useFrame((state) => {
    if (!glowRef.current) return;
    const time = state.clock.elapsedTime;

    const pulse = Math.sin(time * 1.5) * 0.1 + 0.9;
    glowRef.current.material.opacity = pulse * 0.3;
  });

  return (
    <mesh ref={glowRef} position={[0, -0.3, 0.05]}>
      <planeGeometry args={[width + 0.5, 1.2]} />
      <meshBasicMaterial
        color={FIRE_COLORS.ember}
        transparent
        opacity={0.3}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

const EmberBoardGrid3D = ({
  rows = 20,
  columns = 10,
  cellSize = 1,
}) => {
  const halfWidth = (columns * cellSize) / 2;
  const height = rows * cellSize;
  const borderRadius = 0.2;
  const borderThickness = 0.18;
  const borderDepth = 0.3;

  const gridLines = useMemo(() => {
    const points = [];

    // Vertical lines
    for (let i = 0; i <= columns; i++) {
      const x = i * cellSize - halfWidth;
      points.push(new THREE.Vector3(x, 0, -0.1));
      points.push(new THREE.Vector3(x, height, -0.1));
    }

    // Horizontal lines
    for (let i = 0; i <= rows; i++) {
      const y = i * cellSize;
      points.push(new THREE.Vector3(-halfWidth, y, -0.1));
      points.push(new THREE.Vector3(halfWidth, y, -0.1));
    }

    return points;
  }, [rows, columns, cellSize, halfWidth, height]);

  const geometry = useMemo(() => {
    return new THREE.BufferGeometry().setFromPoints(gridLines);
  }, [gridLines]);

  // Border shape
  const borderShape = useMemo(() => {
    const shape = new THREE.Shape();
    const w = columns * cellSize + borderThickness * 2;
    const h = rows * cellSize + borderThickness * 2;
    const r = borderRadius;

    shape.moveTo(-w / 2 + r, -h / 2);
    shape.lineTo(w / 2 - r, -h / 2);
    shape.quadraticCurveTo(w / 2, -h / 2, w / 2, -h / 2 + r);
    shape.lineTo(w / 2, h / 2 - r);
    shape.quadraticCurveTo(w / 2, h / 2, w / 2 - r, h / 2);
    shape.lineTo(-w / 2 + r, h / 2);
    shape.quadraticCurveTo(-w / 2, h / 2, -w / 2, h / 2 - r);
    shape.lineTo(-w / 2, -h / 2 + r);
    shape.quadraticCurveTo(-w / 2, -h / 2, -w / 2 + r, -h / 2);

    const innerW = columns * cellSize;
    const innerH = rows * cellSize;
    const hole = new THREE.Path();
    hole.moveTo(-innerW / 2, -innerH / 2);
    hole.lineTo(innerW / 2, -innerH / 2);
    hole.lineTo(innerW / 2, innerH / 2);
    hole.lineTo(-innerW / 2, innerH / 2);
    hole.lineTo(-innerW / 2, -innerH / 2);
    shape.holes.push(hole);

    return shape;
  }, [rows, columns, cellSize, borderRadius, borderThickness]);

  // Static border - no animation
  const borderRef = useRef();

  return (
    <group>
      {/* Grid lines */}
      <lineSegments geometry={geometry}>
        <lineBasicMaterial
          color={FIRE_COLORS.gridLine}
          opacity={0.25}
          transparent
        />
      </lineSegments>

      {/* Back panel */}
      <mesh position={[0, rows * cellSize / 2, -0.2]} receiveShadow>
        <planeGeometry args={[columns * cellSize, rows * cellSize]} />
        <meshStandardMaterial
          color={FIRE_COLORS.background}
          opacity={0.98}
          transparent
        />
      </mesh>

      {/* Smoke particles */}
      <SmokeParticles />

      {/* Heat shimmer */}
      <HeatShimmer />

      {/* Ember sparks */}
      <EmberSparks />

      {/* Border frame */}
      <mesh ref={borderRef} position={[0, height / 2, 0.05]}>
        <extrudeGeometry
          args={[
            borderShape,
            {
              depth: borderDepth,
              bevelEnabled: true,
              bevelThickness: 0.05,
              bevelSize: 0.03,
              bevelSegments: 2
            }
          ]}
        />
        <meshStandardMaterial
          color={FIRE_COLORS.border}
          emissive={FIRE_COLORS.border}
          emissiveIntensity={0.3}
          metalness={0.35}
          roughness={0.45}
          transparent
          opacity={0.95}
        />
      </mesh>
    </group>
  );
};

export default EmberBoardGrid3D;
